import Phaser from 'phaser';
import { COLORS, FONTS, GAME_HEIGHT, GAME_WIDTH, fontStr } from '@/config';
import { gameState } from '@/state/store';
import { SPILLER_NAVN_DEFAULT } from '@/data/types';
import { voiceOver } from '@/systems/voiceOver';
import { Kolonne } from '@/ui/layout';

type Steg = {
  header: string;
  body: string;
  knapp: string;
};

function lagSteg(spillerNavn: string): Steg[] {
  const navn = spillerNavn.trim().length > 0 ? spillerNavn.trim() : SPILLER_NAVN_DEFAULT;
  return [
    {
      header: 'Telefonen ringer',
      body:
        `«${navn}? Vår gamle elev Bjørn har brent seg ut som sjefredaktør i ` +
        'Forlagshuset. Han trenger noen som tar over. Du har øye for forfatterne ' +
        '— du har gått her selv. Vil du?»\n\n— Kristine, Forfatterskolen',
      knapp: 'Ja, jeg tar jobben  →',
    },
    {
      header: 'Du står utenfor bygget',
      body:
        'Du står utenfor det 7-etasjers bygget i Oslo. Fasaden trenger maling. ' +
        'Ingen tar deg helt seriøst. Du har én aksje. Lykke til.',
      knapp: 'La oss begynne  →',
    },
  ];
}

export class IntroModal {
  private layer?: Phaser.GameObjects.Container;
  private stegIndeks = 0;
  private onFerdigCb?: () => void;
  private onHoppOverCb?: () => void;
  private steg: Steg[] = [];

  constructor(private scene: Phaser.Scene) {}

  open(onFerdig: () => void, onHoppOver: () => void): void {
    if (this.layer) return;
    this.onFerdigCb = onFerdig;
    this.onHoppOverCb = onHoppOver;
    this.stegIndeks = 0;
    this.steg = lagSteg(gameState.spillerForfatter.navn);
    this.render();
    // Kristine snakker det første steget (telefon-replikken)
    voiceOver.les(this.steg[0].body, 'kristine');
  }

  close(): void {
    this.layer?.destroy(true);
    this.layer = undefined;
  }

  get isOpen(): boolean {
    return !!this.layer;
  }

  private render(): void {
    this.layer?.destroy(true);
    this.layer = this.scene.add.container(0, 0).setDepth(300);

    const backdrop = this.scene.add
      .rectangle(0, 0, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.78)
      .setOrigin(0, 0)
      .setInteractive();
    this.layer.add(backdrop);

    const rammeMargin = 60;
    const rammeW = GAME_WIDTH - rammeMargin * 2;
    const rammeH = 1280;
    const rammeX = rammeMargin;
    const rammeY = (GAME_HEIGHT - rammeH) / 2;

    const ramme = this.scene.add
      .rectangle(rammeX, rammeY, rammeW, rammeH, COLORS.wine, 1)
      .setOrigin(0, 0);
    this.layer.add(ramme);

    const innerPad = 16;
    const sheet = this.scene.add
      .rectangle(
        rammeX + innerPad,
        rammeY + innerPad,
        rammeW - innerPad * 2,
        rammeH - innerPad * 2,
        COLORS.beige,
        1,
      )
      .setOrigin(0, 0);
    this.layer.add(sheet);

    const steg = this.steg[this.stegIndeks];
    const innholdsBredde = rammeW - innerPad * 2 - 80;
    const innholdsX = rammeX + innerPad + 40;

    // Layout-primitiv (2026-06-04): ikon → header → body stables fra målt
    // høyde via Kolonne. Ikonet er en stor glyph (fast +150-reservasjon),
    // header/body wrapper og måles. Erstatter løpende `y += height` slik at
    // en wrappet header skyver brødteksten ned uten overlapp.
    const kol = new Kolonne(innholdsX, rammeY + innerPad + 56);

    const ikon = this.scene.add
      .text(innholdsX, 0, this.stegIndeks === 0 ? '☎' : '🏢', {
        fontFamily: FONTS.display,
        fontSize: fontStr(120),
        color: '#862736',
      })
      .setOrigin(0, 0);
    kol.legg(ikon, 0);
    this.layer.add(ikon);
    kol.hoppOver(150 - ikon.height);

    const header = this.scene.add
      .text(innholdsX, 0, steg.header, {
        fontFamily: FONTS.display,
        fontSize: fontStr(64),
        color: '#862736',
        wordWrap: { width: innholdsBredde },
      })
      .setOrigin(0, 0);
    kol.legg(header, 0);
    this.layer.add(header);

    const body = this.scene.add
      .text(innholdsX, 0, steg.body, {
        fontFamily: FONTS.body,
        fontSize: fontStr(32),
        color: '#1a1a1a',
        wordWrap: { width: innholdsBredde },
        lineSpacing: 10,
      })
      .setOrigin(0, 0);
    kol.legg(body, 32);
    this.layer.add(body);

    const knappH = 96;
    const knappW = innholdsBredde;
    const knappX = innholdsX;
    const knappY = rammeY + rammeH - innerPad - knappH - 48;

    const knappBg = this.scene.add
      .rectangle(knappX, knappY, knappW, knappH, COLORS.wine, 1)
      .setOrigin(0, 0);
    const knappTxt = this.scene.add
      .text(knappX + knappW / 2, knappY + knappH / 2, steg.knapp, {
        fontFamily: FONTS.hud,
        fontSize: fontStr(34),
        color: '#ffffff',
        fontStyle: '700',
      })
      .setOrigin(0.5);
    this.layer.add([knappBg, knappTxt]);

    knappBg.setInteractive({ useHandCursor: true });
    knappBg.on('pointerover', () => knappBg.setFillStyle(0x6f1f2c));
    knappBg.on('pointerout', () => knappBg.setFillStyle(COLORS.wine));
    knappBg.on('pointerup', () => this.gaaVidere());

    const hoppOverY = rammeY + rammeH + 20;
    const hoppOver = this.scene.add
      .text(rammeX, hoppOverY, 'Hopp over alt', {
        fontFamily: FONTS.hud,
        fontSize: fontStr(20),
        color: '#cccccc',
      })
      .setOrigin(0, 0)
      .setInteractive({ useHandCursor: true });
    hoppOver.on('pointerover', () => hoppOver.setColor('#ffffff'));
    hoppOver.on('pointerout', () => hoppOver.setColor('#cccccc'));
    hoppOver.on('pointerup', () => {
      voiceOver.stopp();
      this.close();
      this.onHoppOverCb?.();
    });
    this.layer.add(hoppOver);

    const progress = this.scene.add
      .text(
        rammeX + rammeW,
        hoppOverY,
        `${this.stegIndeks + 1} / ${this.steg.length}`,
        {
          fontFamily: FONTS.hud,
          fontSize: fontStr(20),
          color: '#cccccc',
        },
      )
      .setOrigin(1, 0);
    this.layer.add(progress);
  }

  private gaaVidere(): void {
    if (this.stegIndeks < this.steg.length - 1) {
      this.stegIndeks += 1;
      this.render();
      // Andre steg er beskrivende — bruk narrator-stemme
      const profil = this.stegIndeks === 0 ? 'kristine' : 'narrator';
      voiceOver.les(this.steg[this.stegIndeks].body, profil);
    } else {
      voiceOver.stopp();
      this.close();
      this.onFerdigCb?.();
    }
  }
}
